import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link} from 'react-router-dom';
import { faArrowLeftLong, faDownload, faRobot, faUpload } from '@fortawesome/free-solid-svg-icons';
import { SVGGenerationArrowStaticBlue } from '../../components/svgs';
import { fileToBase64, urlToFile } from '../../utils/fileHandling';
import { Config } from '../../config/Config';
import { useAiImageTypesGeneration } from '../../hooks/useAiImageTypesGeneration';
import { stateIconError, stateIconWaiting } from '../../types/ai/states';
import { downloadOriginal } from '../../utils/exportMedia';
import { AuthedImage } from '../../components/ui/AuthedImage';

export const ImageToGifGeneration = () => {
    const { t } = useTranslation('generation');
    const [image,setImage] = useState<{file:any,src:string}|null>(null);
    const [generateGif, setGenerateGif] = useState(false);
    const [inputs,setInputs] = useState<{imgPromptPositive:string,imgPromptNegative:string,animationText:string}>({
        imgPromptPositive:'',
        imgPromptNegative: '',
        animationText:''
    });
    const [generationIMG,stateIMG,setStateIMG,getJobIDIMG,insufficientCreditsDialogIMG] = useAiImageTypesGeneration();
    const [generationGIF,stateGIF,setStateGIF,getJobIDGIF,insufficientCreditsDialogGIF] = useAiImageTypesGeneration();
    
    const [gif, setGif] = useState<string|null>(null);

    const handleGenerationResultImage = async() => {
        let img = generationIMG[0];
        let imgFile = await urlToFile(img,img.replaceAll('\\','/').split('/').slice(-1)[0]);
        setImage({
            file:imgFile,
            src:img
        });
    };

    const handleGenerationResultGIF = () => {
        let gif = generationGIF[0];
        setGif(gif);
    };

    const generateImage = async() => {
        setStateIMG(null);
        const body = { positive_prompt: inputs.imgPromptPositive, negative_prompt: inputs.imgPromptNegative};
        setStateIMG('send');
        let path = 'generate/image'
        try {
            getJobIDIMG('image_gen',path,body);
        } catch(err){
            setStateIMG('error');
        };
    };
    const generateGifFromImage = async () => {
        setGenerateGif(true);
        const prompt = inputs.animationText;
        if(!image){
            setStateGIF('error');
            return;
        }
        const file = image.file;
        const base64Image = await fileToBase64(file);
        const body = {
            "positive_prompt": prompt,
            "image": base64Image
        };
        try {
            setStateGIF('send');
            let path = 'generate/image-to-gif'
            getJobIDGIF('image_gen',path,body);
        } catch(err){
            setStateGIF('error');
        }
    };
    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputs(prev => ({...prev,[name]: value}));
    };
    const uploadFile = async(event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0]  : null;
        if(file){
            const imgSrc = URL.createObjectURL(file);
            setImage({
                'file':file,
                'src':imgSrc
            });
        }
    };
    
    const download = (imageDownload:boolean) => {
        let resource = '';
        if (imageDownload && image){
            resource = image.src;
        }
        else if (gif) {
            resource = gif;
        }
        if(resource === '') return;
        const fileName = resource.replaceAll('\\','/').split('/').slice(-1)[0];
        downloadOriginal(resource, fileName)
            .catch(err => console.error('Download fehlgeschlagen', err));
    };

    useEffect(()=>{
        if(generationIMG.length > 0){
            handleGenerationResultImage();
        }
    },[generationIMG]);
    useEffect(()=>{
        if(generationGIF.length > 0){
            handleGenerationResultGIF();
        }
    },[generationGIF]);
    useEffect(()=>{
        setGenerateGif(false);
    },[image]);

  return (
    <>
        {insufficientCreditsDialogIMG}
        {insufficientCreditsDialogGIF}
        <div className="container-fluid">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <Link 
                            to="/executions" 
                            className="btn btn-outline-secondary btn-sm ms-3"
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title="Back to executions"
                            />
                        </Link>
                    </div>
                </div>
                <div className='flex-grow-1 p-3 overflow-auto'>
                    <div className='row'>
                        <div className='col-5'>
                            {stateIMG 
                                ? stateIMG === 'send'
                                    ? <p className='h4'>{t('state.sending')}</p>
                                    : stateIMG === 'waiting'
                                        ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                                        : stateIMG === 'error'
                                            ? <p className='h4'>{stateIconError} {t('state.error')}</p>
                                                : <></>
                                : <></>
                            }
                        </div>
                        <div className='col'></div>
                        <div className='col-5'>
                            {stateGIF 
                                ? stateGIF === 'send'
                                    ? <p className='h4'>{t('state.sending')}</p>
                                    : stateGIF === 'waiting'
                                        ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                                        : stateGIF === 'error'
                                            ? <p className='h4'>{stateIconError} {t('state.error')}</p>
                                                : <></>
                                : <></>
                            }
                        </div>
                    </div>
                    
                    <div className='row py-2 text-center align-items-center'>
                        <div className='col-5 d-flex flex-wrap p-auto'>
                            { image  &&
                                <>
                                    <div className='row w-100'>
                                        <div className='col-auto'>
                                            <p className='h4 me-2'>{t('image')}</p>
                                        </div>
                                        <div className='col-auto'>
                                            <button
                                                type="button" 
                                                className="btn btn-chat border-0 bg-transparent text-secondary p-0 m-0"
                                                data-bs-toggle="tooltip" 
                                                data-bs-placement="top" 
                                                title={t('download.image')}
                                                >
                                                <FontAwesomeIcon
                                                    icon={faDownload}
                                                    onClick={e =>download(true)}
                                                    size="1x"
                                                    className="pe-1"
                                                />
                                            </button>
                                        </div>
                                        
                                    </div>
                                    <div className='row mt-2'>
                                        <AuthedImage
                                            src={image.src}
                                            alt={`Image: ${image.file.name}`}
                                            className={'rounded d-block'}
                                            style={{height:'520px', width: 'auto', maxWidth:'100%'}}
                                        />
                                    </div>
                                </>
                            }
                        </div>
                        <div className='col'> 
                            {
                                (generateGif) &&
                                 <SVGGenerationArrowStaticBlue/>
                            }

                        </div>
                        <div className='col-5 d-flex flex-wrap p-auto'>
                            { gif &&
                                <>
                                    <div className='row w-100'>
                                        <div className='col-auto'>
                                            <p className='h4 me-2'>{t('gif.generatedGif')} </p>
                                        </div>
                                        <div className='col-auto'>
                                            <button
                                                type="button" 
                                                className="btn btn-chat border-0 bg-transparent text-secondary p-0 m-0"
                                                data-bs-toggle="tooltip" 
                                                data-bs-placement="top" 
                                                title={t('gif.downloadGif')}
                                                >
                                                <FontAwesomeIcon
                                                    icon={faDownload}
                                                    onClick={e =>download(false)}
                                                    size="1x"
                                                    className="pe-1"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    <div className='row mt-2'>
                                        <AuthedImage
                                            src={gif}
                                            alt={`Gif`}
                                            className={'rounded d-block'}
                                            style={{height:'520px', width: 'auto', maxWidth:'100%'}}
                                        />
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
                    
                <div className='row p-3'>
                    <div className='col-6'>
                        <div className='row mt-2'>
                            <div className='col-auto'>
                                <label htmlFor="imgText" className="form-label fw-bold">{t('step1')}</label>
                            </div>
                            <div className='col-auto'>
                                <label htmlFor="fileuploadImage" className=' fw-bold me-2'>
                                    <FontAwesomeIcon icon={faUpload} className='me-2' />
                                    {t('uploadImage')}
                                </label>
                                <input
                                id = 'fileuploadImage'
                                type= 'file'
                                accept='image/png,image/jpg,image/jpeg'
                                onChange={event => uploadFile(event)}
                                hidden
                                />
                                {
                                    image
                                    ? image.file.name
                                    : <></>
                                }
                            </div>
                    
                        </div>
                        <div className='row mt-2'>
                            <div className='col-5'>
                                <textarea
                                className="form-control"
                                id="imgPromptPositive"
                                name='imgPromptPositive'
                                rows={5}
                                value={inputs.imgPromptPositive}
                                onChange={(e) => handleInputChange(e)}
                                placeholder={t('placeholder.describePositive')}
                            />
                            </div>
                            <div className='col-5'>
                                <textarea
                                className="form-control"
                                id="imgPromptNegative"
                                name='imgPromptNegative'
                                rows={5}
                                value={inputs.imgPromptNegative}
                                onChange={(e) => handleInputChange(e)}
                                placeholder={t('placeholder.describeNegative')}
                            />
                            </div>
                            <div className='col-auto  d-flex flex-column justify-content-center text-start'>
                                <button
                                    className="btn btn-primary"
                                    title={t('generateImage')}
                                    onClick={() => generateImage()}
                                >
                                    <FontAwesomeIcon
                                        icon={faRobot}
                                        size='xl'
                                        className='bg-transparent me-2'
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className='col-6'>
                        <div className='row mt-2'>
                            <label htmlFor="animationText" className="form-label fw-bold">{t('step2')}</label>
                        </div>
                        <div className='row mt-2'>
                            <div className='col-10'>
                                <textarea
                                    className="form-control"
                                    id="animationText"
                                    name='animationText'
                                    rows={5}
                                    value={inputs.animationText}
                                    onChange={(e) => handleInputChange(e)}
                                    placeholder={t('placeholder.animate')}
                                />
                            </div>
                            <div className='col-auto d-flex flex-column justify-content-center text-start'>
                                <button
                                    className="btn btn-primary"
                                    title={image===null?t('uploadOrGenerateFirst'):t('generateGif')}
                                    onClick={() => generateGifFromImage()}
                                    disabled={image===null}
                                >
                                    <FontAwesomeIcon
                                        icon={faRobot}
                                        size='xl'
                                        className='bg-transparent me-2'
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>
  );
};

export default ImageToGifGeneration;